import {
  BlockSet,
  BasicController,
  OrthographicView,
  THREE,
  SvgUtils,
  Stage,
} from '@aibee/owlly';
import { vec4, mat4 } from 'gl-matrix';
/**
 * this.stage = new OwllyStage({
 *  container,
 *  dpr: 1,
 *  rotateEnable: true,
 * });
 *
 * this.stage.init();
 * await this.stage.initFloor(svgPath, svgAlignParams);
 */

class OwllyStage {
  /**
   * 构造函数
   * @param {配置} payload
   */
  constructor(payload) {
    this.payload = payload;
    this.zoom_value = 1;
    this.stage = new Stage();
    this.currentBlockSet = null;
    this.polar = 0;
  }

  // 获取地图块
  // eslint-disable-next-line class-methods-use-this
  async getBlockSet(svgPath, svgAlignParams) {
    const shapes = await SvgUtils.parseSvgToShapes(
      svgPath,
      svgAlignParams.transformMatrices.upTrans,
    );

    shapes.forEach((s) => {
      s.updateData({ shapeType: 'line', zIndex: -10.5 });
    });

    return new BlockSet(shapes);
  }

  /**
   * 初始化楼层
   * @param {svg路径} svgPath
   * @param {对齐矩阵} svgAlignParams
   */
  async initFloor(svgPath, svgAlignParams) {
    this.clean();
    const { stage, controller } = this;

    this.svgAlignParams = svgAlignParams;
    const scale = new THREE.Vector3();
    this.scale = scale.x;

    if (this.currentBlockSet) {
      stage.unbindElement(this.currentBlockSet);
    }

    const blocks = await this.getBlockSet(svgPath, svgAlignParams);
    if (!blocks) return;
    this.currentBlockSet = blocks;

    const bbox = new THREE.Box3().setFromObject(blocks);
    const { max, min } = bbox;
    const { container } = this.payload;
    const screenW = container.clientWidth;
    const screenH = container.clientHeight;
    const needScale = Math.min(screenW / (max.x - min.x), screenH / (max.y - min.y));
    this.minZoom = needScale;
    controller.setZoomRange(0.01, 100);
    controller.setCameraPosition(
      new THREE.Vector3((bbox.max.x + bbox.min.x) / 2, (bbox.max.y + bbox.min.y) / 2, 500),
      false,
    );

    controller.setBoundaryEnabled(true);
    controller.setBoundary(
      new THREE.Vector3(bbox.min.x, bbox.min.y, 0),
      new THREE.Vector3(bbox.max.x, bbox.max.y, 0),
    );

    // eslint-disable-next-line no-multi-assign
    this.radius = this.northRadius = SvgUtils.getRotateRadiusToNorth(svgAlignParams.transformMatrices.upTrans);
    this.northDegrees = this.radius * (180 / Math.PI);
    controller.setAzimuthAngle(this.radius);

    controller.setZoom(this.minZoom); // 自适应屏幕

    controller.setPolarRange(0, 0); // 设置纵向旋转范围

    stage.bindElement(this.currentBlockSet);

    this.setCenter(svgAlignParams.img_width / 2, svgAlignParams.img_height / 2, '2d'); // 居中设置
  }

  // 设置初始化
  init() {
    const { container, dpr } = this.payload;

    const { stage } = this;
    const view = new OrthographicView(container);
    view.renderer.setPixelRatio(dpr);
    view.setClearColor(0xffffff, 0);
    view.setViewingHeight(container.clientHeight);

    stage.bindView(view);

    const controller = new BasicController(view);
    controller.setDampingEnabled(false);
    controller.setRotateEnabled(true);
    controller.setRotateMode('orbit');
    controller.setZoomRange(0.01, 100);

    controller.setMouseButtons({
      LEFT: THREE.MOUSE.PAN,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.ROTATE,
    });

    controller.setTouches({
      ONE: THREE.TOUCH.PAN,
      TWO: THREE.TOUCH.DOLLY_ROTATE,
    });

    stage.bindController(controller);

    this.view = view;
    this.controller = controller;
  }

  stageClick(event) {
    const x1 = (event.offsetX / this.payload.container.clientWidth) * 2 - 1;
    const y1 = -(event.offsetY / this.payload.container.clientHeight) * 2 + 1;
    const vec = new THREE.Vector3();
    vec.set(x1, y1, 1);
    vec.unproject(this.view.camera);
    // console.log('2d坐标', this.get2DPositionFrom3D(vec.x, vec.y, 1));
    const point = this.get2DPositionFrom3D(vec.x, vec.y, 1);
    return [point.x, point.y, 1];
  }

  // 获取画布中心
  getCenter() {
    return [this.view.camera.position.x, this.view.camera.position.y];
  }

  getZoom() {
    return this.controller.getZoom();
  }

  getRotate() {
    return this.controller.getAzimuthAngle();
  }

  // 设置地图中心
  setCenter(tx, ty, type = '3d') {
    let x = tx;
    let y = ty;
    if (type === '2d') {
      const arg = this.get3DPositionFrom2D(tx, ty);
      x = arg.x;
      y = arg.y;
    }
    this.center_x = x;
    this.center_y = y;

    this.controller.setCameraLookAt(new THREE.Vector3(x, y, 0), true);
  }

  setZoom(e) {
    this.controller.setZoom(e);
  }

  setRotate(e) {
    this.controller.setAzimuthAngle(e);
  }


  // 3D >> 2D
  get2DPositionFrom3D(x, y, z, downTrans = this.svgAlignParams.transformMatrices.downTrans) {
    const v4 = vec4.create();
    vec4.set(v4, x, y, z, 1);

    const m4 = mat4.fromValues(...downTrans);

    const rs = vec4.create();
    vec4.transformMat4(rs, v4, m4);
    return { x: rs[0], y: rs[1] };
  }

  // 2D >> 3D
  get3DPositionFrom2D(x, y) {
    return SvgUtils.get3DPositionsFrom2D(
      [x, y],
      [...this.svgAlignParams.transformMatrices.upTrans],
    );
  }

  clean() {
    const { stage } = this;
    if (this.currentBlockSet) {
      stage.unbindElement(this.currentBlockSet);
      this.currentBlockSet = null;
    }
  }

  dispose() {
    this.payload = null;
    this.baseLayer = null;
    this.pathLayer = null;
    this.controller = null;
    this.box = null;
    this.view = null;
    this.stage.dispose();
  }
}

// eslint-disable-next-line import/prefer-default-export
export { OwllyStage };
